import type { ReactNode } from 'react'
import type { ParticipantSnapshot } from '@/entities/call'
import { cn } from '@/shared/lib'
import { Avatar } from '@/shared/ui/avatar'
import { Chip } from '@/shared/ui/chip'
import { Icon, type IconName } from '@/shared/ui/icon'

interface ParticipantListProps {
  participants: ParticipantSnapshot[]
  canModerate?: boolean
  onMute?: (participantId: string) => void
  onKick?: (participantId: string) => void
  /**
   * Слот под персональные (не модераторские) контролы удалённого участника.
   * Наполняет его виджет: фича фичу не импортирует.
   */
  renderControls?: (participant: ParticipantSnapshot) => ReactNode
}

export function ParticipantList({
  participants,
  canModerate = false,
  onMute,
  onKick,
  renderControls,
}: ParticipantListProps) {
  if (participants.length === 0)
    return <p className="text-sm text-text-secondary">Пока никого нет</p>

  return (
    <ul className="flex flex-col gap-2">
      {participants.map((p) => (
        <li
          key={p.id}
          className="flex flex-col gap-2 rounded-control border border-border-default px-3 py-2"
        >
          <div className="flex items-center gap-3">
            <Avatar name={p.name} />
            <p className="min-w-0 flex-1 truncate text-sm text-text-primary">
              {p.name}
              {p.isLocal ? ' · Вы' : ''}
            </p>
            {/* Чип ужимается первым (clip, без переполнения): имя и кнопки
                модерации важнее декоративного индикатора. */}
            {p.speaking && (
              <Chip tone="success" className="min-w-0 shrink overflow-hidden whitespace-nowrap">
                говорит
              </Chip>
            )}
            {canModerate && !p.isLocal && (
              <div className="flex shrink-0 gap-1">
                <ModerationButton icon="mic-off" label="Заглушить" onClick={() => onMute?.(p.id)} />
                <ModerationButton
                  icon="close"
                  label="Выгнать"
                  danger
                  onClick={() => onKick?.(p.id)}
                />
              </div>
            )}
          </div>
          {!p.isLocal && renderControls?.(p)}
        </li>
      ))}
    </ul>
  )
}

// Icon-only: полнотекстовые кнопки не влезают в строку узкого сайдбара (320px);
// доступное имя — через aria-label, визуальная подсказка — title.
function ModerationButton({
  icon,
  label,
  danger = false,
  onClick,
}: {
  icon: IconName
  label: string
  danger?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className={cn(
        'inline-flex size-8 items-center justify-center rounded-control border border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent',
        danger
          ? 'text-accent hover:border-border-accent hover:bg-bg-elevated'
          : 'text-text-secondary hover:border-border-strong hover:bg-bg-elevated hover:text-text-primary',
      )}
    >
      <Icon name={icon} size={16} />
    </button>
  )
}
